import { useEffect, useMemo, useState } from 'react';
import { foldersApi } from '@/shared/folders';
import { loadAllGridHeaders } from './settings';
import { loadColumnValues, loadPushedLists } from './computedValues';
import type { GridOptionsLink } from './formats';

/**
 * A table a smartchip/autofill column can borrow its values from: one tab of one
 * spreadsheet the user can open. Tabs of the current document come from the
 * workbook snapshot the grid already holds; the rest are read through Folders,
 * where RLS is what decides "has access".
 */
export interface LinkableTable {
  key: string;
  documentId: string;
  tabId: string;
  documentName: string;
  tabName: string;
  columns: readonly { field: string; name: string }[];
}

export interface WorkbookSnapshot {
  documentId?: string | undefined;
  currentSheetId: string;
  sheets: readonly {
    id: string;
    name: string;
    columns: readonly { field: string; name: string }[];
    rows: readonly Record<string, unknown>[];
  }[];
}

/** The grid a tab's preferences are stored under, and the key renames live in. */
export function gridKeyOfTab(tabId: string): string {
  return `sheet:${tabId}`;
}

/**
 * The name a column is shown under: whoever renamed the header wins, the stored
 * column name is the fallback. Without this a picker lists "Column 1" for a
 * column every user of that table knows as "Destination".
 */
export function columnDisplayName(
  renames: Readonly<Record<string, Record<string, string>>>,
  tabId: string,
  field: string,
  storedName: string,
): string {
  return renames[gridKeyOfTab(tabId)]?.[field] ?? storedName;
}

/** Identity of a link, so values are fetched and cached once per source column. */
export function optionsLinkKey(link: GridOptionsLink): string {
  return `${link.documentId ?? ''}|${link.tabId}|${link.field}`;
}

export function tableKey(documentId: string, tabId: string): string {
  return `${documentId}|${tabId}`;
}

/**
 * How one cell of a linked column is read. A column whose values are computed
 * has nothing in the stored row, so the grid passes a resolver that runs the
 * formula; the default is the stored value.
 */
export type CellResolver = (row: Record<string, unknown>, field: string) => unknown;

const storedValue: CellResolver = (row, field) => row[field];

/** Distinct non-empty cell values, in the order the rows have them. */
export function distinctValues(
  rows: readonly Record<string, unknown>[],
  field: string,
  resolve: CellResolver = storedValue,
): string[] {
  const seen = new Set<string>();
  for (const row of rows) {
    const raw = resolve(row, field);
    if (raw === null || raw === undefined) continue;
    const text = String(raw).trim();
    if (text === '' || seen.has(text)) continue;
    seen.add(text);
  }
  return Array.from(seen);
}

export function sortLinkedValues(values: readonly string[], sort: GridOptionsLink['sort']): string[] {
  return sort === 'alpha' ? [...values].sort((a, b) => a.localeCompare(b)) : [...values];
}

/**
 * Every table the user may link to. The current document's tabs are available
 * immediately; the other documents load in the background, so the dialog opens
 * with the default selection already usable.
 */
export function useLinkableTables(
  enabled: boolean,
  orgId: string | null,
  workbook: WorkbookSnapshot | undefined,
  renames: Readonly<Record<string, Record<string, string>>>,
  /** Calculated columns per tab of the current document, which no stored column list holds. */
  calculated: Readonly<Record<string, readonly { field: string; name: string }[]>> = {},
): { tables: LinkableTable[]; loading: boolean; error: string | null } {
  const currentDocumentId = workbook?.documentId ?? '';
  const [remote, setRemote] = useState<LinkableTable[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const local = useMemo<LinkableTable[]>(() => {
    if (!workbook) return [];
    return workbook.sheets.map((sheet) => ({
      key: tableKey(currentDocumentId, sheet.id),
      documentId: currentDocumentId,
      tabId: sheet.id,
      documentName: '',
      tabName: sheet.name,
      columns: [
        ...sheet.columns.map((column) => ({
          field: column.field,
          name: columnDisplayName(renames, sheet.id, column.field, column.name),
        })),
        ...(calculated[sheet.id] ?? []),
      ],
    }));
  }, [workbook, currentDocumentId, renames, calculated]);

  useEffect(() => {
    if (!enabled || !orgId) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        // One query for every document's renames, rather than one per document.
        const [documents, remoteRenames] = await Promise.all([
          foldersApi.listSpreadsheets(orgId, controller.signal),
          loadAllGridHeaders(orgId, controller.signal),
        ]);
        const others = documents.filter((document) => document.id !== currentDocumentId);
        const tabsPerDocument = await Promise.all(others.map(async (document) => {
          const tabs = await foldersApi.listSheetTabs(document.id, controller.signal);
          return tabs
            .filter((tab) => !tab.isHidden)
            .map<LinkableTable>((tab) => ({
              key: tableKey(document.id, tab.id),
              documentId: document.id,
              tabId: tab.id,
              documentName: document.name,
              tabName: tab.name,
              columns: tab.columns.map((column) => ({
                field: column.id,
                name: columnDisplayName(remoteRenames, tab.id, column.id, column.name),
              })),
            }));
        }));
        if (controller.signal.aborted) return;
        setRemote(tabsPerDocument.flat());
      } catch (loadError) {
        if (!controller.signal.aborted) setError((loadError as Error).message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [enabled, orgId, currentDocumentId]);

  return { tables: [...local, ...remote], loading, error };
}

/**
 * Values of every linked column, keyed by `optionsLinkKey`. A link into the
 * current document reads the workbook snapshot, so it follows edits live; a link
 * into another document is fetched once per link.
 */
export function useLinkedValues(
  links: readonly GridOptionsLink[],
  workbook: WorkbookSnapshot | undefined,
  /** Applied to links into the current document, where the grid's own runtime evaluates formulas. */
  resolveCell?: (tabId: string) => CellResolver,
  /**
   * Where a cross-document list comes from.
   *
   * `pushed` — the snapshot written into this document when the source last
   * changed. The reader needs no access to the source, which is why every grid
   * uses it.
   *
   * `live` — the source column, read directly. Only for the person setting the
   * link up: they can read it, and their preview should show what they are
   * about to borrow rather than an empty list waiting for the first push.
   */
  remoteSource: {
    mode: 'pushed';
    borrowerOf: (link: GridOptionsLink) => { tabId: string; field: string };
  } | { mode: 'live' } = { mode: 'live' },
  /** Bumped when this document's snapshots change, to read them again. */
  revision = 0,
): { values: ReadonlyMap<string, readonly string[]>; loading: boolean; error: string | null } {
  const currentDocumentId = workbook?.documentId ?? '';
  const [fetched, setFetched] = useState<ReadonlyMap<string, readonly string[]>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Links are rebuilt on every render by their holders; compare by identity.
  const signature = links.map(optionsLinkKey).sort().join('\n');

  const local = useMemo(() => {
    const result = new Map<string, readonly string[]>();
    if (!workbook) return result;
    for (const link of links) {
      if (link.documentId && link.documentId !== currentDocumentId) continue;
      const sheet = workbook.sheets.find((candidate) => candidate.id === link.tabId);
      if (!sheet) continue;
      result.set(optionsLinkKey(link), distinctValues(sheet.rows, link.field, resolveCell?.(sheet.id)));
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, workbook, currentDocumentId, resolveCell]);

  useEffect(() => {
    const remote = links.filter((link) => link.documentId && link.documentId !== currentDocumentId);
    if (remote.length === 0) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        let loaded: (readonly [string, readonly string[]])[];
        if (remoteSource.mode === 'pushed') {
          // Reading our own document: the values were pushed here when the
          // source last changed, so no access to it is needed, or granted.
          const pushed = await loadPushedLists(currentDocumentId);
          loaded = remote.map((link) => {
            const borrower = remoteSource.borrowerOf(link);
            return [optionsLinkKey(link), pushed.get(`${borrower.tabId}|${borrower.field}`) ?? []] as const;
          });
        } else {
          loaded = await Promise.all(remote.map(async (link) => (
            [optionsLinkKey(link), await loadColumnValues(link.documentId!, link.tabId, link.field)] as const
          )));
        }
        if (controller.signal.aborted) return;
        setFetched(new Map(loaded));
      } catch (loadError) {
        if (!controller.signal.aborted) setError((loadError as Error).message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, currentDocumentId, remoteSource.mode, revision]);

  const values = useMemo(() => {
    const merged = new Map<string, readonly string[]>(fetched);
    local.forEach((list, key) => merged.set(key, list));
    return merged;
  }, [fetched, local]);

  return { values, loading, error };
}
